import type { CoverCandidate, CoverCandidateText } from '@shared/models/cover-generation-queue.port';

import { COVER_PLACEHOLDER_TWIN } from '../constants/cover-placeholder-twin';

/** What the queue knows about itself when it judges a candidate. */
export interface CoverAutoEligibilityState {
  readonly isEnabled: boolean;
  readonly isKnown: boolean;
  readonly isPaused: boolean;
  readonly isDisabled: boolean;
}

/**
 * The one rule behind every automatic cover generation: the lazy open, the leave and every create trigger.
 *
 * An activity qualifies when it is the caller's own, has no cover and no generation record (so `Skipped` and `Failed`
 * ones are never picked), the client flag is on, the queue has not seen the id this session, the queue is neither
 * paused nor disabled, and the saved name and description are not the empty-activity placeholders.
 *
 * @param candidate The activity as a load or a create trigger knows it.
 * @param state The queue's own state.
 * @returns Whether a generation may be queued for the candidate.
 */
export function isCoverAutoEligible(candidate: CoverCandidate, state: CoverAutoEligibilityState): boolean {
  return (
    candidate.isOwner &&
    candidate.cover === null &&
    candidate.coverGeneration === null &&
    state.isEnabled &&
    !state.isKnown &&
    !state.isPaused &&
    !state.isDisabled &&
    !hasPlaceholderText(candidate.savedText)
  );
}

/**
 * Compares after trimming and exactly, as the server does, so the two sides agree on what counts as untouched.
 *
 * @param savedText The candidate's saved name and description, if it has any.
 * @returns Whether the name or the description is still an empty-activity placeholder.
 */
function hasPlaceholderText(savedText: CoverCandidateText | null): boolean {
  return (
    savedText !== null &&
    (savedText.name.trim() === COVER_PLACEHOLDER_TWIN.NAME ||
      savedText.description.trim() === COVER_PLACEHOLDER_TWIN.DESCRIPTION)
  );
}
